import { useEffect, useMemo, useRef } from 'react';
import { propagate } from 'satellite.js';
import { clockNow, useUI } from '@/state/store';
import { useLoader } from '../store';
import { useSSA } from './store';
import { useMonitoring } from './monitoringStore';
import { BehaviourWatch, meaningfulApproachChange, type BehaviourNotice } from './monitoring';
import { solutionFingerprint } from './quality';
import { parseOrbitalInput } from './orbit';
import { relativeGeometry } from './geometry';
import type { CatalogueSnapshot } from './types';
import type { Discovery } from './discovery';
import type { AnalysisRequest } from './analysis.worker';

export function MonitoringRuntime({ parked }: { parked: boolean }) {
  const dataset = useLoader((s) => s.dataset),
    selectedId = useUI((s) => s.selection.missionId);
  const zones = useSSA((s) => s.zones),
    result = useSSA((s) => s.result),
    enabled = useSSA((s) => s.predictEncounters),
    replay = useSSA((s) => s.replayMode);
  const radius = useSSA((s) => s.radiusKm),
    scope = useSSA((s) => s.scope),
    kinds = useSSA((s) => s.kinds),
    rescan = useMonitoring((s) => s.rescan);
  const watch = useRef(new BehaviourWatch());
  const previous = useRef<{ selectedId: string; snapshot: CatalogueSnapshot; forecast: Discovery } | null>(
    null,
  );
  const equivalent = useMemo(() => {
    const source = dataset?.missions.find((m) => m.id === selectedId)?.trackedObject;
    const fingerprint = source && solutionFingerprint(source);
    return new Set(
      dataset?.missions
        .filter((m) => m.trackedObject && solutionFingerprint(m.trackedObject) === fingerprint)
        .map((m) => m.id) ?? [],
    );
  }, [dataset, selectedId]);
  useEffect(() => {
    watch.current.reset();
    useMonitoring.setState({ notices: [] });
  }, [selectedId, dataset?.catalogueSnapshot?.revision, zones, scope, kinds, radius]);
  useEffect(() => {
    if (parked || !result || result.selectedId !== selectedId || !dataset || radius < zones.close * 1.03)
      return;
    const notices = watch.current.update(result, zones, equivalent);
    useMonitoring.setState((s) => ({
      notices: [...notices, ...s.notices.filter((n) => n.kind === 'Orbit solution changed')].slice(0, 30),
    }));
  }, [parked, result, selectedId, dataset, zones, radius, equivalent]);
  useEffect(() => {
    useMonitoring.setState({ forecast: null, status: 'idle', message: '', revision: '' });
    const snapshot = dataset?.catalogueSnapshot;
    const source = snapshot?.objects.find((o) => 'catalog_' + o.id === selectedId);
    if (!source || !snapshot || parked || !enabled || replay) return;
    let stopped = false,
      worker: Worker | null = null,
      timeout: ReturnType<typeof setTimeout> | undefined,
      lastWall = -Infinity,
      lastStart: number | null = null;
    const cancel = () => {
      worker?.terminate();
      worker = null;
      clearTimeout(timeout);
    };
    const tick = () => {
      if (stopped) return;
      if (document.hidden) {
        if (worker) {
          cancel();
          useMonitoring.setState({ status: 'idle', message: 'Prediction paused while hidden.' });
        }
        return;
      }
      const at = clockNow(),
        wall = Date.now();
      if (
        worker ||
        wall - lastWall < 60000 ||
        (lastStart !== null && at >= lastStart && at - lastStart < 300000)
      )
        return;
      lastWall = wall;
      lastStart = at;
      useMonitoring.setState({ status: 'scanning', message: '' });
      const task = new Worker(new URL('./analysis.worker.ts', import.meta.url), { type: 'module' });
      worker = task;
      const fail = (message: string) => {
        if (stopped || worker !== task) return;
        cancel();
        useMonitoring.setState({ status: 'error', message });
      };
      timeout = setTimeout(
        () => fail('Prediction reached its 25-second limit. Coverage is incomplete; use Scan again.'),
        25000,
      );
      task.onerror = () => fail('Prediction unavailable. Use Scan again.');
      task.onmessage = (event) => {
        if (stopped || worker !== task || event.data.progress) return;
        if (event.data.error) {
          fail(event.data.error);
          return;
        }
        const forecast = event.data.result as Discovery;
        cancel();
        const old = previous.current;
        const changes: BehaviourNotice[] = [];
        if (old?.selectedId === selectedId && old.snapshot.revision !== snapshot.revision) {
          const oldMap = new Map(old.snapshot.objects.map((o) => ['catalog_' + o.id, o]));
          const nowMap = new Map(snapshot.objects.map((o) => ['catalog_' + o.id, o]));
          const oldSource = oldMap.get(selectedId!),
            oldRec = oldSource && parseOrbitalInput({ id: selectedId!, omm: oldSource.omm });
          if (oldSource && oldRec)
            for (const encounter of forecast.encounters.slice(0, 100)) {
              const oldTarget = oldMap.get(encounter.id),
                newTarget = nowMap.get(encounter.id),
                t = encounter.closest.t;
              if (!oldTarget || !newTarget || t < old.forecast.startMs || t > old.forecast.endMs) continue;
              if (
                [oldSource, oldTarget, source, newTarget].some((o) => Math.abs(t - o.epochMs) > 7 * 86400000)
              )
                continue;
              if (
                solutionFingerprint(oldSource) === solutionFingerprint(source) &&
                solutionFingerprint(oldTarget) === solutionFingerprint(newTarget)
              )
                continue;
              const rec = parseOrbitalInput({ id: encounter.id, omm: oldTarget.omm }),
                a = propagate(oldRec, new Date(t)),
                b = rec && propagate(rec, new Date(t));
              if (!a || !b) continue;
              const oldRange = relativeGeometry(a, b).rangeKm,
                newRange = encounter.closest.rangeKm;
              if (meaningfulApproachChange(oldRange, newRange))
                changes.push({
                  id: encounter.id,
                  at,
                  kind: 'Orbit solution changed' as const,
                  detail:
                    'Updated elements reduce predicted separation at the same instant from ' +
                    Math.round(oldRange) +
                    ' to ' +
                    Math.round(newRange) +
                    ' km. Input revision change; not a confirmed manoeuvre.',
                });
            }
        }
        previous.current = { selectedId: selectedId!, snapshot, forecast };
        useMonitoring.setState((s) => ({
          forecast,
          revision: snapshot.revision,
          status: 'ready',
          message: '',
          notices: [...changes, ...s.notices].slice(0, 30),
        }));
      };
      const request: AnalysisRequest = {
        kind: 'discover',
        source: { id: selectedId!, omm: source.omm },
        objects: snapshot.objects.map((o) => ({ id: 'catalog_' + o.id, omm: o.omm })),
        startMs: at,
        endMs: at + 600000,
        radiusKm: zones.close,
      };
      task.postMessage(request);
    };
    const start = setTimeout(tick, 700),
      timer = setInterval(tick, 1500);
    return () => {
      stopped = true;
      cancel();
      clearTimeout(start);
      clearInterval(timer);
    };
  }, [dataset, selectedId, zones, enabled, parked, replay, rescan]);
  return null;
}
